import React, { useRef, useState } from "react";
import { Check, Upload, FileText, Image as ImageIcon } from "lucide-react";
import { cn } from "@/lib/format";
import { fileToDataUrl } from "@/lib/vehicles";

export function Field({ label, hint, className = "", inputClassName = "", ...props }) {
  return (
    <label className={cn("block", className)}>
      {label && <span className="mb-1.5 block text-sm font-medium text-foreground/80">{label}</span>}
      <input
        className={cn(
          "h-12 w-full rounded-xl border border-border bg-card px-4 text-[15px] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-brand-500",
          inputClassName
        )}
        {...props}
      />
      {hint && <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}

export function NativeSelect({ label, options = [], placeholder, value, onChange, disabled, ...props }) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-sm font-medium text-foreground/80">{label}</span>}
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-12 w-full rounded-xl border border-border bg-card px-3 text-[15px] text-foreground outline-none transition-colors focus:border-brand-500 disabled:opacity-50",
          !value && "text-muted-foreground"
        )}
        {...props}
      >
        <option value="">{placeholder || "Select…"}</option>
        {options.map((o) => (
          <option key={o} value={o} className="text-foreground">
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

export function FileField({ label, hint, value, onChange, imageOnly, testID }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const accept = imageOnly ? "image/*" : "image/*,application/pdf";

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      onChange({ name: file.name, dataUrl, isImage: file.type.startsWith("image/") });
    } finally {
      setBusy(false);
    }
  };

  const isImg = value?.isImage;
  return (
    <div>
      {label && <span className="mb-1.5 block text-sm font-medium text-foreground/80">{label}</span>}
      <button
        type="button"
        data-testid={testID}
        onClick={() => inputRef.current?.click()}
        className="flex w-full items-center gap-3 rounded-xl border-2 border-dashed border-border bg-secondary px-4 py-3 text-left transition-colors hover:border-brand-500"
      >
        {value ? (
          isImg ? (
            <img src={value.dataUrl} alt="" className="h-12 w-12 rounded-lg object-cover" />
          ) : (
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent text-brand-600">
              <FileText className="h-6 w-6" />
            </span>
          )
        ) : (
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent text-brand-600">
            {imageOnly ? <ImageIcon className="h-6 w-6" /> : <Upload className="h-6 w-6" />}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-foreground">
            {busy ? "Uploading…" : value ? value.name : `Upload ${hint || "file"}`}
          </span>
          <span className="block text-xs text-muted-foreground">
            {value ? "Tap to replace" : "JPEG, PNG" + (imageOnly ? "" : " or PDF")}
          </span>
        </span>
        {value && <Check className="h-5 w-5 text-success" />}
      </button>
      <input ref={inputRef} type="file" accept={accept} onChange={pick} className="hidden" />
    </div>
  );
}

export function Stepper({ titles, step }) {
  return (
    <div className="mb-5">
      <div className="flex items-center justify-center">
        {titles.map((t, i) => (
          <React.Fragment key={t}>
            <div
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold transition-colors",
                i === step && "border-brand-600 bg-brand-600 text-white",
                i < step && "border-success bg-success text-white",
                i > step && "border-border bg-secondary text-muted-foreground"
              )}
            >
              {i < step ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            {i < titles.length - 1 && (
              <div className={cn("h-0.5 w-9", i < step ? "bg-success" : "bg-border")} />
            )}
          </React.Fragment>
        ))}
      </div>
      <p className="mt-3 text-center text-lg font-bold text-foreground">{titles[step]}</p>
    </div>
  );
}
